// Helpers da tela de pagamento (formas, troco, divisão) — verbatim do antigo
// bloco local do `screen === "payment"`.
import { toast } from "sonner";
import { Banknote, CreditCard, QrCode, Smartphone } from "lucide-react";

export function usePagamento(ctx) {
  const { apiPaymentMethods, cliente, totalAPagar, paymentMethod, setPaymentMethod, isSubmitting, setShowChangeDialog, needsChange, setNeedsChange, changeAmount, setChangeAmount, splitPayment, setSplitPayment, splitPayments, setSplitPayments, setShowSplitModal, splitSelectedMethod, setSplitSelectedMethod, splitAmount, setSplitAmount, setSplitTrocoNeeds, splitTrocoAmount, setSplitTrocoAmount } = ctx;
  const payments = apiPaymentMethods || [
    { id: "cash", label: "Dinheiro", icon: Banknote, isCash: true },
    { id: "credit", label: "Credito", icon: CreditCard, isCash: false },
    { id: "debit", label: "Debito", icon: Smartphone, isCash: false },
    { id: "pix", label: "PIX", icon: QrCode, isCash: false },
  ];

  // Aceita 'cash' (id hardcoded fallback), 'dinheiro'/'Dinheiro' (code do payment_method
  // cadastrado pelo cliente — case-insensitive) e 'dinheiro_<tenant>' (code gerado
  // automaticamente em tenants novos). Sem normalização aqui, o popup de troco não abria.
  const isCashMethod = (m) => {
    const s = (m || "").toLowerCase().trim();
    return s === "cash" || s === "dinheiro" || s.startsWith("dinheiro_");
  };

  const handleSelectPayment = (method) => {
    setPaymentMethod(method);
    if (isCashMethod(method)) {
      setShowChangeDialog(true);
      setNeedsChange(null);
      setChangeAmount('');
    }
  };

  // Split payment helpers
  const splitTotal = splitPayments.reduce((s, p) => s + p.amount, 0);
  const splitRemaining = Math.max(0, totalAPagar - splitTotal);
  const splitComplete = splitPayments.length > 0 && Math.abs(splitTotal - totalAPagar) < 0.01;

  const openSplitModal = () => {
    setSplitSelectedMethod(null);
    setSplitAmount(splitRemaining > 0 ? splitRemaining.toFixed(2) : '');
    setSplitTrocoNeeds(null);
    setSplitTrocoAmount('');
    setShowSplitModal(true);
  };

  const handleAddSplitPayment = () => {
    const amt = parseFloat(splitAmount);
    if (!splitSelectedMethod || !amt || amt <= 0) { toast.error("Selecione forma e valor"); return; }
    if (amt > splitRemaining + 0.01) { toast.error(`Valor máximo: R$ ${splitRemaining.toFixed(2).replace(".", ",")}`); return; }
    const pm = payments.find(p => p.id === splitSelectedMethod);
    // Dinheiro: "Valor recebido" (vazio = exato); maior que a parte = troco.
    const recebido = isCashMethod(splitSelectedMethod) ? (parseFloat(splitTrocoAmount) || 0) : 0;
    if (recebido > 0 && recebido < amt - 0.005) { toast.error("O valor recebido é menor que o desta parte"); return; }
    const troco = recebido > amt + 0.005;
    setSplitPayments(prev => [...prev, {
      method: splitSelectedMethod,
      label: pm?.label || splitSelectedMethod,
      amount: amt,
      troco_precisa: troco,
      troco_valor: troco ? recebido : null,
    }]);
    setShowSplitModal(false);
  };

  const removeSplitPayment = (index) => {
    setSplitPayments(prev => prev.filter((_, i) => i !== index));
  };

  const handleToggleSplit = () => {
    if (!splitPayment) {
      setSplitPayment(true);
      setPaymentMethod(null);
      setSplitPayments([]);
    } else {
      setSplitPayment(false);
      setSplitPayments([]);
    }
  };

  // Normal mode: can submit?
  const canSubmitNormal = !isSubmitting && paymentMethod && !(isCashMethod(paymentMethod) && needsChange === null);
  // Split mode: can submit?
  const canSubmitSplit = !isSubmitting && splitComplete;
  return { payments, isCashMethod, handleSelectPayment, splitTotal, splitRemaining, splitComplete, openSplitModal, handleAddSplitPayment, removeSplitPayment, handleToggleSplit, canSubmitNormal, canSubmitSplit };
}
